import React, { useState, useEffect, useContext, useRef } from 'react'
import { Link } from 'react-router-dom'
import { NavigationContext } from '../../context/navigation-context'
import { LangContext } from '../../context/lang-context'
import useFetch from '../../hooks/fetchMSSQL-hook'
import useForm from '../../hooks/form-hook'
import NoResults from '../../components/NoResults/NoResults'
import LocationForm from '../../components/Modals/LocationForm'
import AcceptForm from '../../components/Modals/AcceptForm'
import SearchFilter from '../../components/Filter/SearchFilter'
import { NotificationManager } from 'react-notifications'
import Tracks from '../../views/ClientView/Routes'

const MachineLocations = () => {
  const { fetchMssqlApi } = useFetch();
  const { setHeaderData } = useContext(NavigationContext);
  const { TRL_Pack } = useContext(LangContext);
  const { form, openForm, closeForm } = useForm();
  const importInputRef = useRef(null);

  const [locations, setLocations] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const [type, setType] = useState("Locations");

  const defaultFilter = {
    name: "machineLocations",
    page: 1,
    rowsPerPage: 50,
    locationId: null,
    locationName: "",
    isActive: "true",
    sortBy: "none",
  };

  const [filter, setFilter] = useState(defaultFilter);

  const [settingsFilter, setSettingsFilter] = useState({
    saveFilters: true,
    autoLoading: true,
    additionalFilter: true,
    flatView: false,
    rowsPerPage: 50,
  });

  const sortDefaults = {
    Name: "ASC",
    Comment: "ASC",
    IsActive: "DESC",
    MachineCount: "DESC",
    FullAddress: "ASC",
  };

  const parseThreeState = (val) => {
    if (val === "true") return true;
    if (val === "false") return false;
    return null 
  }

  const toNumberOrNull = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  };

  const getSortState = () => {
    const [field, dir] = String(filter.sortBy || "").split("|");
    return { field, dir: (dir || "").toUpperCase() };
  };

  const sortState = getSortState();

  const arrow = (field) => {
    if (sortState.field !== field) return "";
    return sortState.dir === "ASC" ? " ▲" : " ▼";
  };

  const handleHeaderSort = (field) => {
    const [curField, curDirRaw] = String(filter.sortBy || "").split("|");
    const curDir = (curDirRaw || "").toUpperCase();

    let nextDir;
    if (curField === field) {
      nextDir = curDir === "ASC" ? "DESC" : "ASC";
    } else {
      nextDir = sortDefaults[field] || "ASC";
    }

    const next = { ...filter, sortBy: `${field}|${nextDir}`, page: 1 };
    setFilter(next);
    getLocations(next, settingsFilter.rowsPerPage, 1);
  };

  const getLocations = (f = filter, pageSize, page) => {
    const pageNo = Number(page ?? f.page ?? 1);
    const rpp = Number(pageSize ?? f.rowsPerPage ?? 50);
    const currentFilter = f || filter;

    fetchMssqlApi(
      "locations",
      {
        method: "GET",
        hideNotification: true,
        params: {
          clientId: sessionStorage.getItem("clientId"),
          locationId: currentFilter.locationId || null,
          locationName: currentFilter.locationName || null,
          isActive: parseThreeState(currentFilter.isActive),
          page: pageNo,
          rowsPerPage: rpp,
          sortBy: currentFilter.sortBy && currentFilter.sortBy !== 'none' ? currentFilter.sortBy : null,
        },
      },
      (res) => {
        const responseRows = res?.rows ?? res ?? [];
        setLocations(responseRows);
        setTotalCount(res?.totalCount ?? responseRows.length);
      }
    );
  };

  const getLocation = () => {
    fetchMssqlApi(`location/${form}`);
  };

  useEffect(() => {
    setHeaderData({ text: TRL_Pack.locations.header });
    getLocations(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const search = (f = filter) => {
    const next = {
      ...filter,
      ...f,
      page: Number(f.page ?? filter.page ?? 1),
      rowsPerPage: Number(
        f.rowsPerPage ?? settingsFilter.rowsPerPage ?? filter.rowsPerPage ?? 50
      ),
      locationId: toNumberOrNull(f.locationId),
    };

    setFilter(next);
    getLocations(next, next.rowsPerPage, next.page);
  };

  const deleteLocation = (id) => {
    fetchMssqlApi(
      `location/${id}`,
      { method: "DELETE" },
      () => getLocations(filter)
    );
  };

  const submitLocation = (evt) => {
    evt.preventDefault();
    const {
      name,
      rent,
      comment,
      town,
      local,
      postDep,
      homeNr,
      index,
      street,
    } = evt.target.elements;

    const payload = {
      Name: name.value,
      Rent: (+rent.value).toFixed(2),
      Comment: comment.value,
      Street: street.value,
      Town: town.value,
      Local: local?.value || "",
      PostDep: postDep.value,
      HomeNr: homeNr.value,
      Index: index.value,
    };

    if (name.value.length < 2 || +rent.value > 10000000) {
      NotificationManager.error(
        "Please write correct data",
        "Validation error",
        3000
      );
      return;
    }

    const path = form === "new" ? "location" : `location/${form}`;
    const method = form === "new" ? "POST" : "PATCH";

    const currentLocation =
      form !== "new"
        ? locations.find((location) => Number(location.LocationId) === Number(form))
        : null;

    const currentIsActive =
      currentLocation?.IsActive === true ||
      currentLocation?.IsActive === 1 ||
      currentLocation?.IsActive === "1" ||
      currentLocation?.IsActive === "true";

    const isActivityButton = evt.nativeEvent?.submitter?.name === "isActive";

    const nextIsActive =
      form === "new"
        ? 1
        : isActivityButton
          ? (currentIsActive ? 0 : 1)
          : (currentIsActive ? 1 : 0);

    fetchMssqlApi(
      path,
      {
        method,
        data: {
          ...payload,
          isActive: nextIsActive
        },
      },
      () => {
        closeForm();
        getLocations(filter);
        if (form !== "new") getLocation();
      }
    );
  };

  const visibleLocations = locations;

  const formatImportErrors = (errors) => {
    if (!Array.isArray(errors) || !errors.length) return null;

    const visibleErrors = errors.slice(0, 6);

    return (
      <div>
        {visibleErrors.map((err, index) => {
          const row = err.RowNumber ? `Wiersz ${err.RowNumber}` : "Plik";
          const field = err.FieldName ? `, ${err.FieldName}` : "";
          const message = err.Message || err.message || "Błąd walidacji";
          const value = err.Value ? ` (${err.Value})` : "";

          return (
            <div key={index}>
              <strong>{row}</strong>
              {field}: {message}{value}
            </div>
          );
        })}

        {errors.length > visibleErrors.length && (
          <div>
            I jeszcze {errors.length - visibleErrors.length} błędów
          </div>
        )}
      </div>
    );
  };

  const handleImportClick = () => {
    if (importInputRef.current) {
      importInputRef.current.click();
    }
  };

  const handleImportChange = (evt) => {
    const file = evt.currentTarget?.files?.[0];

    if (!file) return;

    const clearImportInput = () => {
      if (importInputRef.current) {
        importInputRef.current.value = "";
      }
    };

    const formData = new FormData();
    formData.append("file", file);

    fetchMssqlApi(
      "locations/import",
      { method: "POST", data: formData, hideNotification: true },
      (res) => {
        NotificationManager.success(
          `Zaktualizowano: ${res?.UpdatedCount || 0}, dodano: ${res?.InsertedCount || 0}`,
          "Import lokalizacji"
        );

        clearImportInput();
        getLocations(filter);
      },
      (err) => {
        const data = err?.response?.data || err?.data || err || {};
        const errors = data?.errors || err?.errors;
        const message =
          formatImportErrors(errors) ||
          data?.message ||
          err?.message ||
          "Nie udało się zaimportować pliku";

        NotificationManager.error(message, "Import lokalizacji", 10000);

        clearImportInput();
      }
    );
  };

  return (
    <div>
      <ul className="nav nav-tabs machine-tabs mb-3">
        <li className="nav-item">
          <Link to="/machinelocations">
            <button className="nav-link btn active">Lokalizacje</button>
          </Link>
        </li>
        <li className="nav-item">
          <Link to="/routes">
            <button className="nav-link btn">Trasy</button>
          </Link>
        </li>
      </ul>

      {type === "Locations" && (
        <>
          <SearchFilter
            filterName="machineLocations"
            defaultFilter={defaultFilter}
            onFilterChange={(f) => {
              setFilter((prev) => ({ ...prev, ...f }));
            }}
            onSettingsChange={(s) =>
              setSettingsFilter((prev) => ({ ...prev, ...s }))
            }
            search={search}
            reportList={[{ reportId: 112, reportColor: "primary" }]}
            loading={false}
            totalCount={totalCount}
            dataList={locations}
            forNow
          />

          <input
            ref={importInputRef}
            type="file"
            accept=".xlsx"
            onChange={handleImportChange}
            style={{ display: "none" }}
          />

          <div className="card">
            <div className="card-header">
              <div className="d-flex justify-content-end">
                <button
                  type="button"
                  className="btn btn-outline-success mr-2"
                  onClick={handleImportClick}
                >
                  <i className="fas fa-file-import mr-1" />
                  Import
                </button>

                <button
                  type="button"
                  className="btn text-nowrap btn-outline-secondary"
                  onClick={openForm()}
                >
                  <i className="fas fa-plus" />
                </button>
              </div>
            </div>

          {!locations.length ? (
            <NoResults
              buttonText={TRL_Pack.locations.addProductButton}
              onClick={openForm()}
            />
          ) : (
            <div
                className="overflow-auto custom-scroll"
                style={{ maxHeight: windowHeight - 280 }}
              >
                <table className="table table-striped border">
                  <thead>
                    <tr className="text-center">
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("Name")}
                      >
                        Lokalizacja{arrow("Name")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("Comment")}
                      >
                        Komentarz{arrow("Comment")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("IsActive")}
                      >
                        Aktywna{arrow("IsActive")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("MachineCount")}
                      >
                        Automaty{arrow("MachineCount")}
                      </th>
                      <th
                        style={{ cursor: "pointer", userSelect: "none" }}
                        onClick={() => handleHeaderSort("FullAddress")}
                      >
                        Adres{arrow("FullAddress")}
                      </th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {visibleLocations.map((l) => (
                      <tr key={l.LocationId} className="text-center">
                        <td>{l.Name}</td>
                        <td>{l.Comment}</td>
                        <td>
                          <input
                            type="checkbox"
                            checked={Number(l.IsActive) === 1}
                            readOnly
                          />
                        </td>
                        <td>{l.MachineCount}</td>
                        <td>
                          {l.FullAddress}
                        </td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={openForm(l.LocationId)}
                          >
                            Edytuj
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          )}
          </div>
        </>
      )}

      {type === "Traces" && <Tracks setType={setType} />}

      {form === "acceptModal" && (
        <AcceptForm
          handleClose={closeForm}
          deleteLocation={deleteLocation}
        />
      )}

      {form && form !== "acceptModal" && (
        <LocationForm
          form={form}
          locationData={
            form !== "new"
              ? locations.find((l) => Number(l.LocationId) === Number(form))
              : null
          }
          getLocations={() => getLocations(filter)}
          deleteLocation={deleteLocation}
          handleSubmit={submitLocation}
          handleClose={closeForm}
          locations={locations}
        />
      )}
    </div>
  );
};

export default MachineLocations;